/**
 * Prestatieberekeningen voor hardlopers.
 *
 * - Riegel (1981): T2 = T1 × (D2 / D1)^1,06, voor racevoorspellingen.
 * - VDOT (Daniels & Gilbert): een maat voor loopvorm, waaruit trainingstempo's volgen.
 *   Formules: VO2 = -4,60 + 0,182258·v + 0,000104·v² en
 *   %VO2max = 0,8 + 0,1894393·e^(-0,012778·t) + 0,2989558·e^(-0,1932605·t), met v in m/min en t in minuten.
 */

import type { TranslationKey } from './i18n';

export const RIEGEL_EXPONENT = 1.06;

export interface StandardRace {
  id: string;
  km: number;
  /** Vaste, taalonafhankelijke naam */
  label?: string;
  /** Vertaalde naam */
  labelKey?: TranslationKey;
}

export const STANDARD_RACES: StandardRace[] = [
  { id: '1k', km: 1, label: '1 km' },
  { id: '3k', km: 3, label: '3 km' },
  { id: '5k', km: 5, label: '5 km' },
  { id: '10k', km: 10, label: '10 km' },
  { id: '15k', km: 15, label: '15 km' },
  { id: '10m', km: 16.0934, labelKey: 'race10Miles' },
  { id: 'half', km: 21.0975, labelKey: 'raceHalf' },
  { id: '30k', km: 30, label: '30 km' },
  { id: 'marathon', km: 42.195, labelKey: 'raceMarathon' },
];

export function riegelTime(knownSeconds: number, knownKm: number, targetKm: number, exponent = RIEGEL_EXPONENT): number {
  if (knownSeconds <= 0 || knownKm <= 0 || targetKm <= 0) return 0;
  return knownSeconds * Math.pow(targetKm / knownKm, exponent);
}

const vo2ForVelocity = (v: number) => -4.6 + 0.182258 * v + 0.000104 * v * v;
const fractionOfVo2Max = (minutes: number) =>
  0.8 + 0.1894393 * Math.exp(-0.012778 * minutes) + 0.2989558 * Math.exp(-0.1932605 * minutes);

/** VDOT uit een prestatie. Geeft NaN bij onrealistische invoer (< 3 min of > 8 uur). */
export function vdotFromPerformance(distanceKm: number, seconds: number): number {
  if (distanceKm <= 0 || seconds < 180 || seconds > 8 * 3600) return NaN;
  const minutes = seconds / 60;
  const velocity = (distanceKm * 1000) / minutes;
  return vo2ForVelocity(velocity) / fractionOfVo2Max(minutes);
}

/** Tempo (sec/km) bij een fractie van VO2max (0,59 = 59%). */
export function paceAtVo2Fraction(vdot: number, fraction: number): number {
  const target = vdot * fraction + 4.6;
  const a = 0.000104;
  const b = 0.182258;
  const velocity = (-b + Math.sqrt(b * b + 4 * a * target)) / (2 * a); // m/min
  return 60000 / velocity;
}

/** Tijd (seconden) voor een afstand bij een gegeven VDOT, via bisectie op de VDOT-formule. */
export function timeForVdot(vdot: number, distanceKm: number): number {
  let low = 180;
  let high = 12 * 3600;
  for (let i = 0; i < 60; i++) {
    const mid = (low + high) / 2;
    // Langere tijd = lagere VDOT; zoek de tijd waarbij de VDOT overeenkomt.
    const v = vo2ForVelocity((distanceKm * 1000) / (mid / 60)) / fractionOfVo2Max(mid / 60);
    if (v > vdot) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}

export type ZoneId = 'easy' | 'marathon' | 'threshold' | 'interval' | 'repetition';

export interface TrainingZone {
  id: ZoneId;
  /** Snelste tempo van de zone in sec/km */
  fastPace: number;
  /** Langzaamste tempo van de zone in sec/km (gelijk aan fastPace bij een enkel tempo) */
  slowPace: number;
}

/** Trainingstempo's volgens Daniels. Marathontempo komt uit de gelijkwaardige marathontijd. */
export function trainingZones(vdot: number): TrainingZone[] {
  const marathonPace = timeForVdot(vdot, 42.195) / 42.195;
  return [
    { id: 'easy', fastPace: paceAtVo2Fraction(vdot, 0.74), slowPace: paceAtVo2Fraction(vdot, 0.62) },
    { id: 'marathon', fastPace: marathonPace, slowPace: marathonPace },
    { id: 'threshold', fastPace: paceAtVo2Fraction(vdot, 0.88), slowPace: paceAtVo2Fraction(vdot, 0.88) },
    { id: 'interval', fastPace: paceAtVo2Fraction(vdot, 0.98), slowPace: paceAtVo2Fraction(vdot, 0.98) },
    { id: 'repetition', fastPace: paceAtVo2Fraction(vdot, 1.07), slowPace: paceAtVo2Fraction(vdot, 1.07) },
  ];
}

/**
 * Schatting van de vertraging door warmte. Boven ~12 °C ruim 0,3% per graad, en bij warm weer
 * komt vocht erbij. Het is een richtlijn, geen exacte wetenschap.
 */
export function weatherSlowdown(tempC: number, humidityPct: number): number {
  const heat = 0.003 * Math.max(0, tempC - 12);
  const humidity = tempC > 18 ? 0.0004 * Math.max(0, humidityPct - 60) * (tempC - 18) / 5 : 0;
  return Math.min(0.25, heat + humidity);
}

export function weatherAdjustedPace(paceSecsPerKm: number, tempC: number, humidityPct: number): number {
  return paceSecsPerKm * (1 + weatherSlowdown(tempC, humidityPct));
}

/** Rondetijd in seconden voor een baanronde (standaard 400 m) bij een tempo in sec/km. */
export function lapSeconds(paceSecsPerKm: number, lapMeters = 400): number {
  return (paceSecsPerKm * lapMeters) / 1000;
}

export function speedToPace(speedKmh: number): number {
  return speedKmh > 0 ? 3600 / speedKmh : 0;
}

export function paceToSpeed(paceSecsPerKm: number): number {
  return paceSecsPerKm > 0 ? 3600 / paceSecsPerKm : 0;
}
